import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { BoxGroup } from 'src/app/models/box-group';
import { IBox } from 'src/app/models/interfaces/box.interface';
import { BoxGroupsService } from 'src/app/services/box-groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { FormMode } from '../../../controls/common/form-mode';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { Controls } from '../../../controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from '../../../controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from '../../../controls/forms/management-form/management-form';
import { BoxFormContext } from '../box-form-context';

@Component({
    selector: 'app-box-form',
    templateUrl: './box-form.component.html',
    styleUrls: ['./box-form.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FaIconComponent,
        MatTooltipModule,
        MatSelectModule,
        MatOptionModule,
        MatButtonModule,
        NgClass,
    ]
})
export class BoxFormComponent extends ManagementFormComponent<IBox> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos de l'utilisateur à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('box')
    public dataInput: Required<IBox> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: BoxFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<BoxFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: BoxFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /** Liste des groupes */
    public groups: BoxGroup[];

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('boxUpdated')
    public dataOutput: EventEmitter<IBox> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**Constructeur */
    constructor(
        private boxGroupsService: BoxGroupsService,
        private memoryService: MemoryService
    ) {
        super();
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        this.formGroup.get('viewId').setValue(this.memoryService.getSelectedView()?.id);

        // on récupère les groupes
        this.boxGroupsService.List(null, null, null, null).subscribe((x) => {
            this.groups = x.value;
        });

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.box);
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();

        // L'ordre est non modifiable via le formulaire, la mise à jour se fait uniquement via la liste
        this.formGroup.get('order').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IBox> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            name: new UntypedFormControl(null, Validators.required),
            order: new UntypedFormControl(null),
            groupId: new UntypedFormControl(null, Validators.required),
            viewId: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        // Force l'id de la vue car il n'est pas fourni à l'API car le champ n'existe pas (défini via la liste déroulante de choix de la vue)
        this.dataValue.viewId = this.memoryService.getSelectedView()?.id;
        // Force l'ordre car il n'est pas fourni à l'API car le champ est inactif
        this.dataValue.order = this.formGroup.get('order') ? this.formGroup.get('order').value : 0;

        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
